/*
 * Apply a recommendation from either a product-page sizing guide or the
 * Shape & Size Advisor handoff. The receiver waits for asynchronously loaded
 * product controls, applies fields in dependency order, and reports unmatched
 * values without claiming that every visible option succeeded.
 */
(function () {
    const params = new URLSearchParams(window.location.search);
    const hasUrlHandoff = params.get('kindred_handoff') === '1';
    const expectedProductId = params.get('kindred_product_id');
    const urlRecommendation = {
        Function: params.get('kindred_function'),
        Size: params.get('kindred_size'),
        Width: params.get('kindred_width'),
        Construction: params.get('kindred_construction'),
        'Flex Pattern': params.get('kindred_flex')
    };

    const transferKeys = [
        'kindred_handoff',
        'kindred_shape',
        'kindred_product_id',
        'kindred_function',
        'kindred_size',
        'kindred_width',
        'kindred_flex',
        'kindred_construction'
    ];

    let firstApplied = null;
    let urlComplete = false;
    let urlInFlight = null;
    let urlAttempts = 0;
    let urlRetryTimer = null;

    function readToolResult(root, suffix) {
        const element = root.querySelector(`[id$="ToolResult${suffix}"]`);

        return element ? element.textContent.trim() : '';
    }

    function recommendationFromTool(root) {
        const toolName = root.getAttribute('data-kindred-tool') || '';
        const youthTool = toolName === 'kidred' || toolName === 'elkhorn';
        const flexProfile = readToolResult(root, 'FlexProfile') ||
            (youthTool ? readToolResult(root, 'Flex') : toolName === 'procarve' ? 'Standard' : '');

        return {
            Function: readToolResult(root, 'BoardType'),
            Size: readToolResult(root, 'Length'),
            Width: readToolResult(root, 'Width'),
            Construction: readToolResult(root, 'Composite'),
            'Flex Profile': flexProfile
        };
    }

    function normalize(value) {
        return String(value || '')
            .toLowerCase()
            .replace(/fibreglass/g, 'fiberglass')
            .replace(/carbonfibre/g, 'carbon fiber')
            .replace(/[^a-z0-9]+/g, ' ')
            .trim();
    }

    function unique(values) {
        return Array.from(new Set(values.filter(Boolean)));
    }

    function widthCandidates(value) {
        const profile = String(value || '').split('·')[0].trim();
        const deltaMatch = String(value || '').match(/([+-]\s*\d+(?:\.\d+)?)\s*mm/i);
        const delta = deltaMatch ? deltaMatch[1].replace(/\s+/g, '') : '';

        return unique([
            value,
            profile,
            profile && delta ? `${profile} ${delta}mm` : '',
            delta ? `${delta}mm` : ''
        ]);
    }

    function candidatesFor(name, value) {
        const normalized = normalize(value);

        if (name === 'Product Type') {
            if (normalized.includes('split')) {
                return unique([value, 'Splitboard', 'Split']);
            }

            if (normalized.includes('ski')) {
                return unique([value, 'Ski', 'Skis']);
            }

            if (normalized.includes('snowboard') || normalized.includes('board')) {
                return unique([value, 'Snowboard', 'Solid Board', 'Solid']);
            }
        }

        if (name === 'Function') {
            return normalized.includes('split')
                ? ['Split +$300 CAD', 'Splitboard', 'Split']
                : ['Solid', 'Solid Board'];
        }

        if (name === 'Size') {
            const length = String(value || '').match(/\d+(?:\.\d+)?/);
            return unique([value, length && length[0], length && `${length[0]} cm`]);
        }

        if (name === 'Width') {
            return widthCandidates(value);
        }

        if (name === 'Flex Profile' || name === 'Flex Pattern') {
            if (normalized.includes('extra stiff')) {
                return unique([value, 'Extra Stiff', 'Extra Stiff Flex']);
            }

            if (normalized.includes('extra soft')) {
                return unique([value, 'Extra Soft', 'Extra Soft Flex']);
            }

            if (normalized.includes('firm') || normalized.includes('stiff')) {
                return unique([value, 'Firm', 'Stiff', 'Firm Flex', 'Stiff Flex']);
            }

            if (normalized.includes('soft')) {
                return unique([value, 'Soft', 'Soft Flex']);
            }

            if (normalized.includes('standard')) {
                return unique([value, 'Standard', 'Standard Flex', 'Regular']);
            }
        }

        if (name === 'Construction') {
            if (normalized.includes('race')) {
                return unique([value, 'Race Construction', 'Race Build +$449 CAD', 'Race Build']);
            }

            if (normalized.includes('titanal') && normalized.includes('carbon')) {
                return unique([
                    value,
                    'Titanal Reinforced Carbon Fiber',
                    'Titanal Reinforced Carbon',
                    'Titanal Reinforced Triaxial Carbon Fiber',
                    'Carbon Fiber/Titanal Edge',
                    'Carbon/Titanal Edge'
                ]);
            }

            if (normalized.includes('titanal') && normalized.includes('fiberglass')) {
                return unique([
                    value,
                    'Titanal Reinforced Fiberglass',
                    'Titanal Reinforced Triaxial Fiberglass',
                    'Fiberglass/Titanal Edge'
                ]);
            }

            if (normalized.includes('triaxial') && normalized.includes('fiberglass')) {
                return unique([value, 'Triaxial Fiberglass']);
            }

            if (normalized.includes('biaxial') && normalized.includes('fiberglass')) {
                return unique([value, 'Biaxial Fiberglass']);
            }

            if (normalized.includes('carbon')) {
                return unique([
                    value,
                    'Carbon Fiber',
                    'Carbon'
                ]);
            }

            if (normalized.includes('fiberglass')) {
                return unique([
                    value,
                    'Fiberglass',
                    'Triaxial Fiberglass',
                    'Biaxial Fiberglass'
                ]);
            }
        }

        return [value];
    }

    function candidatesWithGroupPrefixes(name, value) {
        const candidates = candidatesFor(name, value);
        const prefixes = name === 'Construction'
            ? ['Construction']
            : (name === 'Flex Profile' || name === 'Flex Pattern')
                ? ['Flex Pattern', 'Flex Profile', 'Flex']
                : [];
        const prefixed = candidates.slice();

        prefixes.forEach(function (prefix) {
            candidates.forEach(function (candidate) {
                prefixed.push(`${prefix}: ${candidate}`);
            });
        });

        return unique(prefixed);
    }

    function withoutTrailingPrice(value) {
        return String(value || '')
            .replace(/\s+\d+(?:\s+\d+)*(?:\s+(?:cad|usd))?$/, '')
            .trim();
    }

    function candidateMatcher(candidates, exactOptionName) {
        const wanted = candidates.map(normalize).filter(Boolean);
        const exactWanted = exactOptionName
            ? wanted.map(withoutTrailingPrice)
            : [];

        return function (value) {
            const raw = normalize(value);

            if (exactOptionName) {
                return exactWanted.indexOf(withoutTrailingPrice(raw)) !== -1;
            }

            return wanted.some(function (item) {
                return raw === item ||
                    raw.indexOf(`${item} `) === 0 ||
                    item.indexOf(`${raw} `) === 0;
            });
        };
    }

    function optionContext(element) {
        const group = element.closest(
            '[data-child-name], fieldset, [data-option-name], [data-name], ' +
            '.giftship-option-container, .giftship-option, .product-form__input, ' +
            '.option-selector, .product-option'
        );
        const label = element.id
            ? document.querySelector(`label[for="${CSS.escape(element.id)}"]`)
            : element.closest('label');
        const heading = group && group.querySelector(
            'legend, .label, .option-name, [data-option-label], .gs__label'
        );

        return [
            element.name,
            element.id,
            element.getAttribute('aria-label'),
            element.getAttribute('data-option-name'),
            group && group.getAttribute('data-child-name'),
            group && group.getAttribute('data-option-name'),
            label && label.textContent,
            heading && heading.textContent
        ].map(normalize).join(' ');
    }

    function contextMatches(context, name) {
        const wanted = normalize(name);

        if (name === 'Construction') {
            return context.includes('construction') || context.includes('contruction');
        }

        if (name === 'Flex Profile' || name === 'Flex Pattern') {
            return context.includes('flex') || context.includes('stiffness');
        }

        if (name === 'Function') {
            return context.includes('function') || context.includes('board format');
        }

        if (name === 'Size') {
            return context.includes('size') || context.includes('length');
        }

        return context.includes(wanted);
    }

    function notifyOption(element) {
        element.dispatchEvent(new Event('input', { bubbles: true }));
        element.dispatchEvent(new Event('change', { bubbles: true }));
    }

    function giftshipOptionValue(input) {
        return input.getAttribute('data-child-option-name') ||
            input.getAttribute('data-child-option-value') ||
            input.value;
    }

    function giftshipInputsWithin(scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;

        return Array.from(contextRoot.querySelectorAll(
            'input.gs__product-element, input[data-child-option]'
        )).filter(function (input) {
            return !input.closest('.kindred-sizing-tool, [data-custom-build-intake]');
        });
    }

    function giftshipGroupInputs(input, groupName, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const inputs = giftshipInputsWithin(contextRoot);
        const sameContext = function (candidate) {
            return contextMatches(optionContext(candidate), groupName);
        };
        const sameForm = function (candidate) {
            return !input.form || !candidate.form || candidate.form === input.form;
        };

        /* Native radio/checkbox names are the most precise group boundary. */
        if (input.name) {
            const sameName = inputs.filter(function (candidate) {
                return candidate.name === input.name && sameForm(candidate) && sameContext(candidate);
            });

            if (sameName.length > 1) return sameName;
        }

        /* Giftship sometimes exposes its group key on a wrapper instead. */
        const keyed = input.closest('[data-child-name], [data-option-name], [data-name]');
        if (keyed) {
            const key = keyed.getAttribute('data-child-name') ||
                keyed.getAttribute('data-option-name') || keyed.getAttribute('data-name');
            const attribute = keyed.hasAttribute('data-child-name')
                ? 'data-child-name'
                : keyed.hasAttribute('data-option-name') ? 'data-option-name' : 'data-name';
            const sameKey = inputs.filter(function (candidate) {
                const owner = candidate.closest(`[${attribute}]`);
                return owner && owner.getAttribute(attribute) === key &&
                    sameForm(candidate) && sameContext(candidate);
            });

            if (sameKey.length > 1) return sameKey;
        }

        /* Find the smallest semantic container holding more than one choice. */
        const groupSelector = [
            'fieldset',
            '[data-child-name]',
            '[data-option-name]',
            '[data-name]',
            '.giftship-option-container',
            '.product-form__input',
            '.option-selector',
            '.product-option'
        ].join(', ');
        let ancestor = input.parentElement;

        while (ancestor) {
            if (ancestor.matches && ancestor.matches(groupSelector)) {
                const descendants = inputs.filter(function (candidate) {
                    return ancestor.contains(candidate) && sameForm(candidate) && sameContext(candidate);
                });

                if (descendants.length > 1) return descendants;
            }

            if (ancestor === contextRoot || ancestor === document.body) break;
            ancestor = ancestor.parentElement;
        }

        /* Last resort: constrain the operation to Giftship controls with the same group label. */
        return inputs.filter(function (candidate) {
            return sameForm(candidate) && sameContext(candidate);
        });
    }

    function clearConflictingGiftshipInputs(input, groupName, matches, scope) {
        const conflicts = giftshipGroupInputs(input, groupName, scope).filter(function (candidate) {
            return candidate !== input && !candidate.disabled && candidate.checked &&
                !matches(giftshipOptionValue(candidate));
        });

        conflicts.forEach(function (candidate) {
            if (!candidate.isConnected || !candidate.checked) return;

            /* A checkbox click preserves any Giftship click handlers and native event order. */
            if (candidate.type === 'checkbox') {
                candidate.click();
                if (!candidate.isConnected || !candidate.checked) return;
            }

            /* Checked radios cannot be toggled off with click, so clear and notify explicitly. */
            candidate.checked = false;
            notifyOption(candidate);
        });

        return conflicts;
    }

    function applyProductOption(name, value, kind, scope) {
        const semanticName = kind || name;
        const matches = candidateMatcher(
            candidatesWithGroupPrefixes(semanticName, value),
            normalize(semanticName) === 'construction'
        );
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const outsideSizingTool = function (element) {
            return !element.closest('.kindred-sizing-tool, [data-custom-build-intake]');
        };
        const giftshipInputs = giftshipInputsWithin(contextRoot);

        for (let i = 0; i < giftshipInputs.length; i += 1) {
            const input = giftshipInputs[i];
            const optionValue = giftshipOptionValue(input);

            if (!input.disabled && contextMatches(optionContext(input), name) && matches(optionValue)) {
                clearConflictingGiftshipInputs(input, name, matches, contextRoot);
                if (!input.isConnected || input.disabled) {
                    return null;
                }
                if (!input.checked) {
                    input.click();
                }
                if ((input.type === 'radio' || input.type === 'checkbox') && !input.checked) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = input;
                }
                return input;
            }
        }

        const selects = Array.from(contextRoot.querySelectorAll('select'))
            .filter(outsideSizingTool);

        for (let i = 0; i < selects.length; i += 1) {
            const select = selects[i];

            if (select.disabled || !contextMatches(optionContext(select), name)) {
                continue;
            }

            const option = Array.from(select.options).find(function (item) {
                return matches(item.value) || matches(item.textContent);
            });

            if (option) {
                select.value = option.value;
                notifyOption(select);
                if (select.value !== option.value) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = select;
                }
                return select;
            }
        }

        const standardInputs = Array.from(contextRoot.querySelectorAll(
            'input[type="radio"], input[type="checkbox"]'
        )).filter(outsideSizingTool);

        for (let i = 0; i < standardInputs.length; i += 1) {
            const input = standardInputs[i];
            const label = input.closest('label') ||
                (input.id ? document.querySelector(`label[for="${CSS.escape(input.id)}"]`) : null);

            if (
                !input.disabled &&
                contextMatches(optionContext(input), name) &&
                (matches(input.value) || matches(label && label.textContent))
            ) {
                if (!input.checked) {
                    input.click();
                }
                if (!input.checked) {
                    continue;
                }
                if (!firstApplied) {
                    firstApplied = input;
                }
                return input;
            }
        }

        return null;
    }

    function selectedGiftshipMatches(entry, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const semanticName = entry.kind || entry.property;
        const matches = candidateMatcher(
            candidatesWithGroupPrefixes(semanticName, entry.value),
            normalize(semanticName) === 'construction'
        );
        const matched = [];
        const seen = new Set();

        entry.groups.forEach(function (groupName) {
            contextRoot.querySelectorAll('input.gs__product-element, input[data-child-option]').forEach(function (input) {
                const optionValue = giftshipOptionValue(input);
                if (
                    !input.disabled && input.checked &&
                    contextMatches(optionContext(input), groupName) && matches(optionValue) &&
                    !seen.has(input)
                ) {
                    seen.add(input);
                    matched.push(input);
                }
            });

            contextRoot.querySelectorAll('select.gs__product-element, select[data-child-option], [data-child-name] select').forEach(function (select) {
                const selected = select.options && select.options[select.selectedIndex];
                if (
                    !select.disabled && selected &&
                    contextMatches(optionContext(select), groupName) &&
                    (matches(selected.value) || matches(selected.textContent)) &&
                    !seen.has(select)
                ) {
                    seen.add(select);
                    matched.push(select);
                }
            });
        });

        return matched;
    }

    function selectedGiftshipConflicts(entry, matches, scope) {
        const contextRoot = scope && scope.querySelectorAll ? scope : document;
        const semanticName = entry.kind || entry.property;
        const desired = candidateMatcher(
            candidatesWithGroupPrefixes(semanticName, entry.value),
            normalize(semanticName) === 'construction'
        );
        const conflicts = [];
        const seen = new Set();

        entry.groups.forEach(function (groupName) {
            matches.filter(function (input) {
                return input && input.tagName === 'INPUT' &&
                    contextMatches(optionContext(input), groupName);
            }).forEach(function (input) {
                giftshipGroupInputs(input, groupName, contextRoot).forEach(function (candidate) {
                    if (
                        candidate !== input && !candidate.disabled && candidate.checked &&
                        !desired(giftshipOptionValue(candidate)) && !seen.has(candidate)
                    ) {
                        seen.add(candidate);
                        conflicts.push(candidate);
                    }
                });
            });
        });

        return conflicts;
    }

    function verifyFields(fields, options) {
        const settings = options || {};
        const entries = (fields || []).filter(function (entry) {
            return entry && Boolean(entry.value) && entry.value !== '—' && entry.groups && entry.groups.length;
        });
        const context = productContext(settings.productId);
        const form = productForm(settings.productId);

        if ((settings.productId && !context) || !form) {
            return {
                ok: false,
                missing: entries.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
                missingEntries: entries.slice(),
                ambiguous: [],
                form: form
            };
        }

        const searchRoot = context || document;
        const missing = [];
        const ambiguous = [];

        entries.forEach(function (entry) {
            const matches = selectedGiftshipMatches(entry, searchRoot);
            const conflicts = selectedGiftshipConflicts(entry, matches, searchRoot);

            if (!matches.length) missing.push(entry);
            if (matches.length > 1 || conflicts.length) ambiguous.push(entry);
        });

        return {
            ok: missing.length === 0 && ambiguous.length === 0,
            missing: missing.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
            missingEntries: missing,
            ambiguous: ambiguous.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
            form: form
        };
    }

    function formProductContext(form) {
        return form && form.closest(
            '[data-kindred-commerce-context][data-product-id], ' +
            '[data-section-type="product"][data-product-id]'
        );
    }

    function formProductId(form) {
        const context = formProductContext(form);
        return context ? String(context.getAttribute('data-product-id') || '') : '';
    }

    function productFormScore(form) {
        let score = 0;

        if (form.querySelector('input.gs__product-element, input[data-child-option]')) score += 100;
        if (form.querySelector('#gsAppContainer, .gs__product-app-container')) score += 60;
        if (form.closest('[data-kindred-commerce-context]')) score += 30;
        if (form.closest('[data-section-type="product"]')) score += 20;
        if (form.classList.contains('product-single__form')) score += 10;
        return score;
    }

    function productForm(productId) {
        const expectedProductId = productId ? String(productId) : '';
        const forms = Array.from(document.querySelectorAll('form[action*="/cart/add"]'))
            .filter(function (form) {
                if (form.closest('.kindred-sizing-tool, [data-custom-build-intake]')) return false;
                if (!form.querySelector('[name="id"]')) return false;
                return !expectedProductId || formProductId(form) === expectedProductId;
            })
            .sort(function (left, right) {
                return productFormScore(right) - productFormScore(left);
            });

        return forms[0] || null;
    }

    function productContext(productId) {
        const form = productForm(productId);

        if (!form) return productId ? null : document;
        return formProductContext(form) || form;
    }

    function setProperty(form, name, value) {
        let input = form.querySelector(`input[data-kindred-handoff="${CSS.escape(name)}"]`);

        if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = `properties[${name}]`;
            input.dataset.kindredHandoff = name;
            form.appendChild(input);
        }

        input.value = value;
    }

    function wait(ms) {
        return new Promise(function (resolve) {
            window.setTimeout(resolve, ms);
        });
    }

    function cleanTransferUrl() {
        const cleanUrl = new URL(window.location.href);

        transferKeys.forEach(function (key) {
            cleanUrl.searchParams.delete(key);
        });

        if (cleanUrl.hash === '#kindred-product-options') {
            cleanUrl.hash = '';
        }

        window.history.replaceState(
            {},
            document.title,
            cleanUrl.pathname + cleanUrl.search + cleanUrl.hash
        );
    }

    function showStatus(recommendation, appliedCount, missing, embeddedStatus) {
        const attached = Boolean(productForm() && missing.length);
        const fullyApplied = appliedCount > 0 && missing.length === 0;
        const partiallyApplied = appliedCount > 0 && missing.length > 0;
        const missingNames = missing.map(function (entry) { return entry[0]; });

        if (embeddedStatus) {
            if (fullyApplied) {
                embeddedStatus.textContent = 'The recommended product options were applied.';
            } else if (partiallyApplied && attached) {
                embeddedStatus.textContent = `Available options were applied. Please confirm: ${missingNames.join(', ')}.`;
            } else if (attached) {
                embeddedStatus.textContent = 'Your recommendation is ready for confirmation.';
            } else {
                embeddedStatus.textContent = 'The recommended options could not be applied. Please review your selections and try again.';
            }

            return embeddedStatus;
        }

        const existing = document.getElementById('kindred-sizing-handoff-status');
        const status = existing || document.createElement('div');
        const values = Object.entries(recommendation)
            .filter(function (entry) { return Boolean(entry[1]); })
            .map(function (entry) { return `${entry[0]}: ${entry[1]}`; });
        const host = document.querySelector(
            '[data-section-type="product"] .product-single__meta, ' +
            '[data-section-type="product"], .product-single__meta'
        ) || productForm();

        status.id = 'kindred-sizing-handoff-status';
        status.setAttribute('role', 'status');
        status.setAttribute('aria-live', 'polite');
        status.style.cssText = [
            'margin:0 0 20px',
            'padding:14px 16px',
            'border:1px solid #9ec7d8',
            'border-radius:10px',
            'background:#eaf7fc',
            'color:#111',
            'font-size:14px',
            'line-height:1.45'
        ].join(';');
        status.textContent = '';
        const lead = document.createElement('strong');
        lead.textContent = fullyApplied
            ? 'Your selections were applied.'
            : partiallyApplied
                ? 'Available selections were applied.'
                : 'Your selections were carried over.';
        status.appendChild(lead);
        status.appendChild(document.createTextNode(
            ` ${values.join(' · ')}${attached ? ` ${missingNames.join(', ')} still ${missingNames.length === 1 ? 'needs' : 'need'} confirmation.` : ''}`
        ));

        if (!existing && host) {
            host.insertBefore(status, host.firstChild);
        }

        return status;
    }

    async function applyFields(fields, options) {
        const settings = options || {};
        const entries = (fields || []).filter(function (entry) {
            return entry && Boolean(entry.value) && entry.value !== '—' && entry.groups && entry.groups.length;
        });
        const missing = [];
        const ambiguous = [];
        const appliedElements = [];
        let appliedCount = 0;
        const displayValues = {};

        entries.forEach(function (entry) {
            displayValues[entry.property || entry.kind || entry.groups[0]] = entry.value;
        });

        firstApplied = null;

        const context = productContext(settings.productId);
        if (settings.productId && !context) {
            return {
                ok: false,
                applied: 0,
                appliedElements: [],
                missing: entries.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
                missingEntries: entries.slice(),
                ambiguous: [],
                form: null
            };
        }
        const searchRoot = context || document;

        for (let attempt = 0; attempt < 80; attempt += 1) {
            if (searchRoot.querySelector('input.gs__product-element, input[data-child-option]')) {
                break;
            }

            await wait(100);
        }

        for (let i = 0; i < entries.length; i += 1) {
            const entry = entries[i];
            let applied = null;

            for (let attempt = 0; attempt < 24 && !applied; attempt += 1) {
                for (let groupIndex = 0; groupIndex < entry.groups.length && !applied; groupIndex += 1) {
                    applied = applyProductOption(entry.groups[groupIndex], entry.value, entry.kind, searchRoot);
                }
                if (!applied) {
                    await wait(125);
                }
            }

            if (applied) {
                appliedCount += 1;
                appliedElements.push(applied);
                await wait(180);
            } else {
                missing.push(entry);
            }
        }

        if (settings.verify) {
            await wait(250);
            entries.forEach(function (entry) {
                const matches = selectedGiftshipMatches(entry, searchRoot);
                const conflicts = selectedGiftshipConflicts(entry, matches, searchRoot);
                const alreadyMissing = missing.indexOf(entry) !== -1;
                if (!matches.length && !alreadyMissing) missing.push(entry);
                if (matches.length > 1 || conflicts.length) ambiguous.push(entry);
            });
        }

        const form = productForm(settings.productId);

        if (form && settings.attachMissing !== false) {
            missing.forEach(function (entry) {
                setProperty(form, entry.property || entry.kind || entry.groups[0], entry.value);
            });
        }

        const missingPairs = missing.map(function (entry) {
            return [entry.property || entry.kind || entry.groups[0], entry.value];
        });
        const status = settings.showStatus === false ? null : showStatus(
            displayValues,
            appliedCount,
            missingPairs,
            settings.embeddedStatus || null
        );

        if (settings.cleanUrl) {
            cleanTransferUrl();
        }
        document.dispatchEvent(new CustomEvent('kindred.sizing.applied', {
            detail: {
                applied: appliedCount,
                attached: form && settings.attachMissing !== false ? missing.length : 0,
                missing: missingPairs.map(function (entry) { return entry[0]; }),
                shape: settings.shape || params.get('kindred_shape')
            }
        }));

        if (settings.scroll !== false) window.setTimeout(function () {
            const destination = firstApplied || status;

            if (destination) {
                destination.scrollIntoView({ behavior: 'smooth', block: 'center' });
            }
        }, 250);

        return {
            ok: missing.length === 0 && ambiguous.length === 0 && Boolean(form),
            applied: appliedCount,
            appliedElements: appliedElements,
            missing: missingPairs.map(function (entry) { return entry[0]; }),
            missingEntries: missing,
            ambiguous: ambiguous.map(function (entry) { return entry.property || entry.kind || entry.groups[0]; }),
            form: form
        };
    }

    function applyRecommendation(recommendation, options) {
        const fields = Object.entries(recommendation || {})
            .filter(function (entry) { return Boolean(entry[1]) && entry[1] !== '—'; })
            .map(function (entry) {
                return {
                    property: entry[0],
                    kind: entry[0],
                    groups: [entry[0]],
                    value: entry[1]
                };
            });
        return applyFields(fields, options);
    }

    function scheduleUrlRetry() {
        if (urlComplete || urlAttempts >= 5 || urlRetryTimer) return;
        urlRetryTimer = window.setTimeout(function () {
            urlRetryTimer = null;
            startUrlHandoff();
        }, Math.min(2200, 400 + (urlAttempts * 350)));
    }

    function startUrlHandoff() {
        if (!hasUrlHandoff || urlComplete || urlInFlight) return;

        urlAttempts += 1;
        urlInFlight = applyRecommendation(urlRecommendation, {
            attachMissing: false,
            cleanUrl: false,
            productId: expectedProductId,
            shape: params.get('kindred_shape'),
            verify: true
        }).then(function (result) {
            if (result.ok) {
                urlComplete = true;
                cleanTransferUrl();
                return result;
            }
            scheduleUrlRetry();
            return result;
        }).catch(function (error) {
            scheduleUrlRetry();
            if (window.console && typeof window.console.warn === 'function') {
                window.console.warn('Kindred sizing handoff will retry after an option-loading error.', error);
            }
            return null;
        }).finally(function () {
            urlInFlight = null;
        });

        return urlInFlight;
    }

    document.addEventListener('click', function (event) {
        const target = event.target instanceof Element ? event.target : null;
        const applyButton = target && target.closest(
            '.kindred-sizing-tool [data-apply-giftship]'
        );

        if (!applyButton || applyButton.disabled || applyButton.dataset.kindredApplying === 'true') {
            return;
        }

        const root = applyButton.closest('.kindred-sizing-tool');

        if (!root) {
            return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();
        applyButton.dataset.kindredApplying = 'true';
        applyButton.disabled = true;

        const embeddedStatus = root.querySelector('[id$="ToolGiftshipStatus"]');

        if (embeddedStatus) {
            embeddedStatus.textContent = 'Applying your recommendation…';
        }

        applyRecommendation(recommendationFromTool(root), {
            cleanUrl: false,
            embeddedStatus: embeddedStatus,
            shape: root.getAttribute('data-kindred-tool') || ''
        }).catch(function () {
            if (embeddedStatus) {
                embeddedStatus.textContent = 'The recommendation could not be applied. Please try again.';
            }
        }).finally(function () {
            applyButton.disabled = false;
            delete applyButton.dataset.kindredApplying;
        });
    }, true);

    window.KindredGiftshipHandoff = Object.assign(window.KindredGiftshipHandoff || {}, {
        applyRecommendation: applyRecommendation,
        applyFields: applyFields,
        verifyFields: verifyFields,
        productContext: productContext,
        productForm: productForm,
        setProperty: setProperty
    });
    window.KindredGiftshipHandoffTest = Object.assign(window.KindredGiftshipHandoffTest || {}, {
        matchesOption: function (name, value, actual) {
            return candidateMatcher(
                candidatesWithGroupPrefixes(name, value),
                normalize(name) === 'construction'
            )(actual);
        }
    });

    if (hasUrlHandoff) {
        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', startUrlHandoff, { once: true });
        } else {
            startUrlHandoff();
        }

        document.addEventListener('giftship.loaded', startUrlHandoff);
        document.addEventListener('giftship.reloaded', startUrlHandoff);
    }
})();
